import { useRef, useEffect } from 'react';

/**
 * Hook personalizado para gerenciar scroll automático de accordions
 * @returns {Object} Objeto com refs e funções de scroll
 */
const useAccordionScroll = () => {
  const accordionRef = useRef(null);

  /**
   * Scroll para um accordion específico dentro de um container
   * @param {HTMLElement} element - Elemento do accordion
   * @param {number} offset - Offset adicional (padrão: 20px)
   */
  const scrollToAccordion = (element, offset = 20) => {
    if (element && accordionRef.current) {
      const container = accordionRef.current;
      const elementTop = element.offsetTop;
      const containerTop = container.offsetTop;
      const scrollTop = container.scrollTop;

      // Calcular a posição para scroll
      const targetScrollTop = scrollTop + elementTop - containerTop - offset;

      // Scroll suave para o accordion
      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }
  };

  /**
   * Scroll para centralizar um elemento na tela
   * @param {HTMLElement} element - Elemento para centralizar
   * @param {number} offset - Offset adicional (padrão: 0)
   */
  const scrollToElement = (element, offset = 0) => {
    if (element) {
      const elementTop = element.offsetTop;
      const windowHeight = window.innerHeight;
      const elementHeight = element.offsetHeight;

      // Calcular a posição para centralizar o elemento na tela
      const targetScrollTop = elementTop - (windowHeight / 2) + (elementHeight / 2) + offset;

      // Scroll suave para o elemento
      window.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
    }
  };

  /**
   * Scroll para um accordion quando ele é aberto
   * @param {boolean} isOpen - Se o accordion está aberto
   * @param {number} delay - Delay antes do scroll (padrão: 100ms)
   */
  const scrollToAccordionOnOpen = (isOpen, delay = 100) => {
    if (isOpen && accordionRef.current) {
      setTimeout(() => {
        scrollToElement(accordionRef.current);
      }, delay);
    }
  };

  return {
    accordionRef,
    scrollToAccordion,
    scrollToElement,
    scrollToAccordionOnOpen
  };
};

export default useAccordionScroll;
